// ProvenanceGraph — the "single source of truth" hero figure.
//
// Additive sibling of LineagePanel (LineagePanel itself is untouched). Reuses the
// same SVG grammar: a fixed viewBox, the shared lin-arrow / lin-arrow-warn arrow
// markers, `.lineage-node tone-*` rects, cubic-bezier `.lineage-edge` paths, and
// the optional rich-tooltip controller.
//
// Layout: one horizontal SOURCE-SYSTEM SWIMLANE per distinct contributing system.
// Each lane flows left → right: source record node → transform (rollup) node →
// convergence. Every lane's aggregated edge converges on ONE large canonical
// entity node on the right — the literal picture of many enterprise systems
// becoming a single governed record. Individual field edges fan into the lane's
// transform node; dense lanes roll their field labels up to "top N + ＋K more"
// while STILL drawing every edge (no 10-edge cap).

import { useMemo } from 'react';
import { useOptionalRichTooltipController } from '../richTooltips/RichTooltipProvider.jsx';
import { fieldLabel, humanizeEnum, sourceSystemLabel } from '../../lib/fieldLabels.js';
import '../../features/admin/provenanceGraph.css';

// Figure geometry (viewBox width is fixed; height scales with lane count so a
// dense entity stays legible). Tuned so the default 3–6 lane figure reads well at
// 1600×900 — the guide's hero size.
const W = 1600;
const TARGET_H = 900;
// A sparse (1–2 lane) figure otherwise inherited the full 900px hero height and
// rendered hundreds of pixels of empty canvas. Clamp the height between a compact
// floor and the 900px hero cap so sparse graphs shrink while the dense 5-lane
// hero still fills 1600×900.
const MIN_H = 320;
const SRC_X = 72;
const SRC_W = 194;
const SRC_H = 54;
const TF_X = 566;
const TF_W = 214;
const TF_H = 58;
const CANON_X = 1316;
const CANON_W = 236;
const CANON_H = 124;
const BAND_X = 34;
const BAND_W = 1150;
const FIELD_X = 812;

// Deterministic tone rotation for lane accents, drawn from the shared tone tokens.
const LANE_TONES = ['blue', 'sage', 'amber', 'primary', 'slate'];

// Group key for a lane. Real seed edges only carry `connectorId`; staged/enriched
// edges may carry `sourceSystem`. Fall back through both so grouping is robust.
function laneKeyFor(row) {
  return (
    row.sourceSystem ||
    row.connectorId ||
    row.sourceEntityType ||
    row.sourceSystemId ||
    'unknown'
  );
}

// Human-readable lane label from a connector/source key. Route through the
// shared sourceSystemLabel so the finance lane reads "FMIS / Tableau" (like every
// other surface) instead of the raw "PMRT FINANCE" connector-id strip. Fall back
// to the historical prefix-strip only when the key has no friendly mapping.
function laneLabelFor(key) {
  const mapped = sourceSystemLabel(key);
  if (mapped) return mapped;
  const raw = String(key || 'Source')
    .replace(/^(CONN|SRC|ART)-/i, '')
    .replace(/[_-]+/g, ' ')
    .trim();
  return raw || 'Source';
}

function sanitizeTestId(value) {
  return String(value || 'unknown').replace(/[^A-Za-z0-9_-]+/g, '-');
}

function compact(value, max = 30) {
  const raw = String(value ?? '').trim();
  if (!raw) return '';
  return raw.length > max ? `${raw.slice(0, max - 1)}…` : raw;
}

function mostCommon(values) {
  const counts = new Map();
  let best = null;
  let bestCount = 0;
  for (const v of values) {
    if (!v) continue;
    const next = (counts.get(v) || 0) + 1;
    counts.set(v, next);
    if (next > bestCount) {
      bestCount = next;
      best = v;
    }
  }
  return best;
}

export function ProvenanceGraph({
  edges = [],
  focusEntityId = null,
  selectedEdgeId = null,
  onSelect,
  onSelectEdge,
  onDrill,
  topFieldsPerLane = 6,
  ariaLabel = 'Provenance graph — source systems converging on a single canonical record',
}) {
  const tooltip = useOptionalRichTooltipController();

  const model = useMemo(() => {
    const canonicalId =
      mostCommon(edges.map((e) => e.canonicalEntityId || e.targetEntityId)) || focusEntityId;
    const canonicalType =
      mostCommon(edges.map((e) => e.canonicalEntityType || e.targetEntityType)) || 'Canonical entity';

    // Group edges into lanes by source system, preserving first-seen order.
    const laneMap = new Map();
    for (const row of edges) {
      const key = laneKeyFor(row);
      if (!laneMap.has(key)) laneMap.set(key, []);
      laneMap.get(key).push(row);
    }
    const lanes = Array.from(laneMap.entries()).map(([key, rows], i) => {
      const uniqueFields = [];
      const seen = new Set();
      for (const r of rows) {
        const f = r.canonicalField || r.sourceField || r.transformation || r.id;
        if (seen.has(f)) continue;
        seen.add(f);
        uniqueFields.push({ field: f, transformation: r.transformation, conflict: !!r.conflictFlag });
      }
      return {
        key,
        label: laneLabelFor(key),
        rows,
        uniqueFields,
        hasConflict: rows.some((r) => r.conflictFlag),
        tone: LANE_TONES[i % LANE_TONES.length],
        sourceRecordId:
          rows[0]?.sourceRecordId || rows[0]?.sourceEntityId || rows[0]?.connectorId || key,
      };
    });

    const laneCount = Math.max(lanes.length, 1);
    // Lane height scales down as lanes multiply, clamped for legibility.
    const laneHeight = Math.max(130, Math.min(212, Math.floor(760 / laneCount)));
    const blockHeight = laneCount * laneHeight;
    // Adaptive height: compact for sparse graphs, capped at the 900px hero size
    // for dense ones (the 5-lane hero lands at 900 exactly).
    const H = Math.min(TARGET_H, Math.max(MIN_H, blockHeight + 150));
    const startY = Math.max(70, (H - blockHeight) / 2);
    const canonY = H / 2;

    lanes.forEach((lane, i) => {
      const y0 = startY + i * laneHeight;
      lane.y0 = y0;
      lane.height = laneHeight;
      lane.mid = y0 + laneHeight / 2;
    });

    return { canonicalId, canonicalType, lanes, H, canonY };
  }, [edges, focusEntityId]);

  const { canonicalId, canonicalType, lanes, H, canonY } = model;

  // Shared trigger-props helper: wires the rich tooltip when a provider is
  // present, always guarantees our onClick + data-testid contract otherwise.
  function triggerFor({ payload, onClick, ariaLabel: label }) {
    if (tooltip && payload) {
      return tooltip.getTriggerProps({
        surfaceId: payload.surfaceId,
        payload,
        testIdPrefix: 'rich-tooltip',
        onDrill,
        ariaLabel: label,
        onClick,
      });
    }
    return { onClick };
  }

  function handleEdgeClick(row) {
    if (row.conflictFlag) {
      onDrill?.({
        kind: 'drawer',
        drawerTarget: 'reconciliation',
        params: {
          edgeId: row.id,
          targetEntityType: row.canonicalEntityType || row.targetEntityType || null,
          targetEntityId: row.canonicalEntityId || row.targetEntityId || null,
        },
      });
      return;
    }
    onSelectEdge?.(row);
  }

  const canonicalPayload = edges.find((e) => e.targetNodeTooltip)?.targetNodeTooltip || null;

  return (
    <div className="provenance-graph" data-testid="provenance-graph" role="img" aria-label={ariaLabel}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} preserveAspectRatio="xMidYMid meet">
        <defs>
          <marker
            id="lin-arrow"
            viewBox="0 0 8 8"
            refX="7"
            refY="4"
            markerWidth="6"
            markerHeight="6"
            orient="auto"
          >
            <path d="M0 0 L8 4 L0 8 z" fill="#1961a1" />
          </marker>
          <marker
            id="lin-arrow-warn"
            viewBox="0 0 8 8"
            refX="7"
            refY="4"
            markerWidth="6"
            markerHeight="6"
            orient="auto"
          >
            <path d="M0 0 L8 4 L0 8 z" fill="#c0524d" />
          </marker>
        </defs>

        {/* Column captions */}
        <text x={SRC_X + SRC_W / 2} y={38} textAnchor="middle" className="provenance-col">
          Source systems
        </text>
        <text x={TF_X + TF_W / 2} y={38} textAnchor="middle" className="provenance-col">
          Transform &amp; reconcile
        </text>
        <text x={CANON_X + CANON_W / 2} y={38} textAnchor="middle" className="provenance-col">
          Single source of truth
        </text>

        {/* Lanes */}
        {lanes.map((lane) => {
          const rows = lane.rows;
          const count = rows.length;
          const pad = 26;
          const span = lane.height - pad * 2;
          const aggMidX = (TF_X + TF_W + CANON_X) / 2;
          const fanMidX = (SRC_X + SRC_W + TF_X) / 2;
          const shownFields = lane.uniqueFields.slice(0, topFieldsPerLane);
          const overflow = lane.uniqueFields.length - shownFields.length;

          return (
            <g
              key={lane.key}
              className={`provenance-lane tone-${lane.tone}${lane.hasConflict ? ' has-conflict' : ''}`}
              data-testid={`provenance-lane-${sanitizeTestId(lane.key)}`}
            >
              {/* Lane band */}
              <rect
                className="provenance-band"
                x={BAND_X}
                y={lane.y0 + 6}
                width={BAND_W}
                height={lane.height - 12}
                rx={16}
              />
              <text className="provenance-lane-label" x={BAND_X + 18} y={lane.y0 + 26}>
                {lane.label}
              </text>
              <text className="provenance-lane-count" x={BAND_X + 18} y={lane.y0 + 44}>
                {count} field {count === 1 ? 'edge' : 'edges'}
                {lane.hasConflict ? ' · Conflict' : ''}
              </text>

              {/* Individual field edges: source → transform, fanning in */}
              {rows.map((row, j) => {
                const frac = count <= 1 ? 0.5 : (j + 0.5) / count;
                const srcY = lane.y0 + pad + frac * span;
                const d = `M ${SRC_X + SRC_W} ${srcY} C ${fanMidX} ${srcY}, ${fanMidX} ${lane.mid}, ${TF_X} ${lane.mid}`;
                const isConflict = !!row.conflictFlag;
                const cls = `lineage-edge${isConflict ? ' conflict' : ''}${
                  selectedEdgeId === row.id ? ' selected' : ''
                }`;
                const tp = triggerFor({
                  payload: row.edgeTooltip || null,
                  onClick: () => handleEdgeClick(row),
                  ariaLabel: `${row.canonicalField || row.id} lineage edge`,
                });
                return (
                  <path
                    key={row.id || j}
                    d={d}
                    className={cls}
                    markerEnd={isConflict ? 'url(#lin-arrow-warn)' : 'url(#lin-arrow)'}
                    role="button"
                    tabIndex={0}
                    style={{ cursor: 'pointer' }}
                    {...tp}
                    data-testid={`lineage-edge-${row.id}`}
                  />
                );
              })}

              {/* Aggregated lane → canonical edge */}
              <path
                className={`lineage-edge provenance-agg${lane.hasConflict ? ' conflict' : ''}`}
                d={`M ${TF_X + TF_W} ${lane.mid} C ${aggMidX} ${lane.mid}, ${aggMidX} ${canonY}, ${CANON_X} ${canonY}`}
                markerEnd={lane.hasConflict ? 'url(#lin-arrow-warn)' : 'url(#lin-arrow)'}
                data-testid={`provenance-agg-${sanitizeTestId(lane.key)}`}
              />

              {/* Source node */}
              {(() => {
                const tp = triggerFor({
                  payload: rows[0]?.sourceNodeTooltip || null,
                  onClick: () => onSelect?.(lane.sourceRecordId, { kind: 'source', record: rows[0] }),
                  ariaLabel: `${lane.label} source record`,
                });
                return (
                  <g
                    className={`lineage-node tone-${lane.tone}`}
                    data-testid={`provenance-source-${sanitizeTestId(lane.key)}`}
                    role="button"
                    tabIndex={0}
                    style={{ cursor: 'pointer' }}
                    {...tp}
                  >
                    <rect x={SRC_X} y={lane.mid - SRC_H / 2} width={SRC_W} height={SRC_H} rx={12} />
                    <text
                      className="lineage-node-label"
                      x={SRC_X + SRC_W / 2}
                      y={lane.mid - 2}
                      textAnchor="middle"
                    >
                      {compact(lane.label, 22)}
                    </text>
                    <text
                      className="lineage-node-sub"
                      x={SRC_X + SRC_W / 2}
                      y={lane.mid + 15}
                      textAnchor="middle"
                    >
                      {compact(lane.sourceRecordId, 24)}
                    </text>
                  </g>
                );
              })()}

              {/* Transform / rollup node */}
              <g
                className="lineage-node tone-amber provenance-transform"
                data-testid={`provenance-transform-${sanitizeTestId(lane.key)}`}
              >
                <rect x={TF_X} y={lane.mid - TF_H / 2} width={TF_W} height={TF_H} rx={12} />
                <text
                  className="lineage-node-label"
                  x={TF_X + TF_W / 2}
                  y={lane.mid - 2}
                  textAnchor="middle"
                >
                  Map · reconcile
                </text>
                <text
                  className="lineage-node-sub"
                  x={TF_X + TF_W / 2}
                  y={lane.mid + 15}
                  textAnchor="middle"
                >
                  {count} field{count === 1 ? '' : 's'} → canonical
                </text>
              </g>

              {/* Field roll-up labels */}
              {shownFields.map((f, k) => {
                const rowY =
                  lane.mid -
                  ((Math.min(shownFields.length, topFieldsPerLane) - 1) * 16) / 2 +
                  k * 16;
                return (
                  <text
                    key={f.field}
                    className={`provenance-field${f.conflict ? ' conflict' : ''}`}
                    x={FIELD_X}
                    y={rowY}
                  >
                    {f.conflict ? '⚠ ' : ''}
                    {compact(fieldLabel(f.field), 22)}
                  </text>
                );
              })}
              {overflow > 0 ? (
                <text
                  className="provenance-field-more"
                  x={FIELD_X}
                  y={lane.mid + shownFields.length * 8 + 14}
                >
                  +{overflow} more
                </text>
              ) : null}
            </g>
          );
        })}

        {/* Canonical convergence node — the single source of truth */}
        {canonicalId ? (
          <g
            className="lineage-node tone-primary provenance-canonical"
            data-testid={`provenance-canonical-${canonicalId}`}
            role="button"
            tabIndex={0}
            style={{ cursor: 'pointer' }}
            {...triggerFor({
              payload: canonicalPayload,
              onClick: () => onSelect?.(canonicalId, { kind: 'canonical', entityType: canonicalType }),
              ariaLabel: `${canonicalId} canonical record`,
            })}
          >
            <rect
              x={CANON_X}
              y={canonY - CANON_H / 2}
              width={CANON_W}
              height={CANON_H}
              rx={18}
            />
            <text
              className="provenance-canonical-eyebrow"
              x={CANON_X + CANON_W / 2}
              y={canonY - CANON_H / 2 + 24}
              textAnchor="middle"
            >
              CANONICAL
            </text>
            <text
              className="lineage-node-label provenance-canonical-label"
              x={CANON_X + CANON_W / 2}
              y={canonY + 2}
              textAnchor="middle"
            >
              {compact(canonicalId, 24)}
            </text>
            <text
              className="lineage-node-sub"
              x={CANON_X + CANON_W / 2}
              y={canonY + 24}
              textAnchor="middle"
            >
              {compact(humanizeEnum(canonicalType), 26)}
            </text>
          </g>
        ) : null}
      </svg>
    </div>
  );
}
